import React, { useEffect, useState } from 'react';
import * as D from './index.styled';
import DataTable from '@/components/@common/DataTable';
import { InquiryAllResponse, InquiryData, InquiryResponse } from '@/@types/InquiryData';
import { ResData } from '@/@types';
import axiosRequest from '@/api';
import { useLocation, useParams } from 'react-router-dom';
import { Button, TablePaginationConfig, Tooltip } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import * as XLSX from 'xlsx';
import { DownloadOutlined } from '@ant-design/icons';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);
function Inquiry() {
  const { id: campaignId } = useParams<{ id: string }>(); // id 값을 바로 추출합니다.
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState<number>();
  const [inquiryData, setInquiryData] = useState<InquiryData[]>([]);
  const location = useLocation();

  const handlePaginationChange = (pagination: TablePaginationConfig) => {
    setCurrentPage(pagination.current || 1);
    setPageSize(pagination.pageSize || 10);
    fetchInquiryData();
  };

  useEffect(() => {
    fetchInquiryData();
  }, [campaignId, currentPage, pageSize]);

  function truncateText(text: string, limit: number) {
    if (text && text.length > limit) {
      return text.substring(0, limit) + '...';
    }
    return text;
  }

  const formatDateTime = (datetime: string): string => {
    const date = new Date(datetime);
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1 < 10 ? '0' : ''}${date.getMonth() + 1}`;
    const day = `${date.getDate() < 10 ? '0' : ''}${date.getDate()}`;
    const hours = `${date.getHours() < 10 ? '0' : ''}${date.getHours()}`;
    const minutes = `${date.getMinutes() < 10 ? '0' : ''}${date.getMinutes()}`;
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  const statusMap: { [key: string]: string } = {
    r: '지급',
    p: '참여',
    e: '에러',
    c: '기타',
  };

  const handleStatusToggle = async (id: number | undefined, currentStatus: string) => {
    if (typeof id === 'number') {
      let newStatus;
      if (currentStatus === null) {
        // '미확인' 상태
        newStatus = 'c'; // '확인중'으로 상태 변경
      } else if (currentStatus === 'c') {
        // '확인중' 상태
        newStatus = 'f'; // '처리완료'로 상태 변경
      } else if (currentStatus === 'f') {
        // '처리완료' 상태
        newStatus = 'c'; // '확인중'으로 상태 변경
      }

      try {

        const response = await axiosRequest.requestAxios<ResData<InquiryResponse>>('post', `/inquiry_status/${id}`, { status: newStatus });
        if (response.status === 200) {
          fetchInquiryData();
        } else {
          console.error('Error updating status:', response);
        }
      } catch (error) {
        console.error('Error updating status:', error);
      }
    } else {
      console.error('Invalid id:', id);
    }
  };

  const columns = [
    {
      title: 'No',
      dataIndex: 'no',
      key: 'no',
      width: 50,
    },
    {
      title: '문의 종류',
      dataIndex: 'type',
      key: 'type',
      render: (text: string) => statusMap[text],
      width: 100,
    },
    {
      title: '문의 내용',
      dataIndex: 'content',
      key: 'content',
      render: (text: string) => <Tooltip title={text}>{truncateText(text, 20)}</Tooltip>,
    },
    {
      title: '고객 정보',
      dataIndex: 'uuid',
      key: 'uuid',
      render: (text: string) => <Tooltip title={text}>{truncateText(text, 20)}</Tooltip>,
    },
    {
      title: '첨부 이미지',
      dataIndex: 'file_url',
      key: 'file_url',
      render: (text: string) => (
        <Tooltip title={text}>
          <a href={text} target="_blank" rel="noopener noreferrer">
            {truncateText(text, 40)}
          </a>
        </Tooltip>
      ),
    },
    {
      title: 'email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: '등록 시간',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text: string) => formatDateTime(text),
    },
    {
      title: '참여 상태',
      dataIndex: 'participation_status',
      key: 'participation_status',
      render: (text: string) => {
        let status_str = '미참여'
        if (text === 'f') {
          status_str = '참여 실패';
        } else if (text === 'p') {
          status_str = '미적립 참여';
        } else if (text === 'i') {
          status_str = '최초 참여';
        } else if (text === 'r') {
          status_str = '참여 및 적립 완료';
        } else {
          status_str = '미참여';
        }
        return status_str;
      }
    },
    {
      title: '상태 (Click)',
      key: 'status',
      render: (text: string, record: InquiryData) => {
        let buttonText;
        if (record.status === null) {
          buttonText = '미확인';
        } else if (record.status === 'c') {
          buttonText = '확인중';
        } else {
          buttonText = '처리완료';
        }

        return (
          <D.StatusBtn status={record.status} onClick={() => handleStatusToggle(record.id, record.status)}>
            {buttonText}
          </D.StatusBtn>
        );
      },
    },
  ];

  const fetchInquiryData = async () => {
    try {
      const response = await axiosRequest.requestAxios<ResData<InquiryResponse>>(
        'get',
        `/campaign/inquiry/${campaignId}?page=${currentPage}&pageSize=${pageSize}`,
      );
      const { total_count, result } = response.data;

      const mappedData = result.map((item, index) => ({
        ...item,
        key: `${(currentPage - 1) * pageSize + index + 1}`, // key를 문자열로 변환
        no: (currentPage - 1) * pageSize + index + 1, // 번호 부여
      }));

      setTotalCount(total_count);
      setInquiryData(mappedData);
    } catch (error) {
      console.error('Error fetching inquiry data:', error);
    }
  };

  const createCampaignInfoString = (data: any) => {
    const parts = [];

    // 캠페인 제목
    if (data.title) {
      parts.push(`캠페인명: ${data.title}`);
    }

    // 캠페인 기간
    if (data.start_date || data.end_date) {
      parts.push(`캠페인 기간: ${data.start_date || ''} ~ ${data.end_date || ''}`);
    }

    // 통계 정보
    const stats = [
      ['문의 종류', data.type],
      ['문의 내용', data.content],
      ['고객 정보', data.uuid],
      ['첨부 이미지', data.file_url],
      ['이메일', data.email],
      ['등록 시간', data.created_at],
      ['상태', data.status],
    ];

    return stats.reduce((acc, [label, value], index) => {
      if (value !== undefined) {
        acc.push([`${label}: ${value.toLocaleString()}`]);
      }
      return acc;
    }, parts);
  };

  const handleExcelDownload = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const campaign_id = campaignId;

      const response = await axiosRequest.requestAxios<ResData<InquiryAllResponse>>(
        'get',
        `/campaign/all_inquiries/${campaignId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      // 파일 이름 생성 로직 (백엔드 로직을 참고)
      let titlePart = '';
      if (response.data.title) {
        titlePart = response.data.title;
      }

      if (titlePart === '') {
        titlePart = '전체';
      }
      const fileDate = dayjs().format('YYYYMMDD');
      const fileName = `wereward_inquries_${titlePart}_${fileDate}.xlsx`;

      // 엑셀 데이터 생성
      const title = response.data.title;

      // 엑셀 첫번째 줄에 캠페인 정보 추가 글자 크기 증가
      const campaignInfo = `캠페인 정보: ${title} (${response.data.start_date} ~ ${response.data.end_date})`;

      const data = response.data.result.map((row, index) => {
        let status_str = '미참여'
        if (row.participation_status === 'f') {
          status_str = '참여 실패';
        } else if (row.participation_status === 'p') {
          status_str = '미적립 참여';
        } else if (row.participation_status === 'i') {
          status_str = '최초 참여';
        } else if (row.participation_status === 'r') {
          status_str = '참여 및 적립 완료';
        }
        return {
          'No': index + 1,
          '고객고유정보': row.uuid,
          'Image URl': row.file_url,
          '등록시간': dayjs(row.created_at).tz('Asia/Seoul').format('YYYY-MM-DD HH:mm:ss'),
          '이메일': row.email,
          '문의내용': row.content,
          '상태': status_str,
        }
      });
      // 엑셀 생성 및 다운로드 로직
      const worksheet = XLSX.utils.json_to_sheet([]);

      // 캠페인 정보 추가
      const infoLines = createCampaignInfoString(response.data);
      console.log(infoLines);
      XLSX.utils.sheet_add_aoa(worksheet, infoLines.map(line => [line]), { origin: 'A1' });

      // 각 행에 스타일 적용
      infoLines.forEach((_, index) => {
        const cellRef = `A${index + 1}`;
        worksheet[cellRef].s = {
          font: { bold: true, sz: 12 },
          alignment: { horizontal: 'left' }
        };

        // 각 행을 A부터 D까지 병합
        if (!worksheet['!merges']) worksheet['!merges'] = [];
        worksheet['!merges'].push({
          s: { r: index, c: 0 },
          e: { r: index, c: 3 }
        });
      });

      // 데이터 추가 (캠페인 정보 다음 행부터)
      XLSX.utils.sheet_add_json(worksheet, data, {
        origin: `A${infoLines.length + 1}`
      });

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'log report');

      // 엑셀 파일 다운로드
      XLSX.writeFile(workbook, fileName);
    } catch (error) {
      // message.error('엑셀 다운로드에 실패했습니다.');
      console.error('Error downloading excel:', error);
    }
  };

  return (
    <>
      <D.Wrapper>
        <D.LinkNavigate>
          <D.StyledLink
            to={`/admin/campaign`}
            onClick={e => {
              if (location.pathname === `/admin/campaign`) e.preventDefault();
            }}
            current={location.pathname === `/admin/campaign`}
          >
            캠페인
          </D.StyledLink>
          <D.StyledLink
            to={`/admin/campaign/inquiry/${campaignId}`}
            onClick={e => {
              if (location.pathname === `/admin/campaign/inquiry/${campaignId}`) e.preventDefault();
            }}
            current={location.pathname === `/admin/campaign/inquiry/${campaignId}`}
          >
            문의사항
          </D.StyledLink>

        </D.LinkNavigate>
        <D.DashBoardTitle>{inquiryData[0]?.title}</D.DashBoardTitle>

        <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="primary" onClick={handleExcelDownload} icon={<DownloadOutlined />} disabled={false} >
            excel 다운로드
          </Button>
        </div>



        <D.TableContainer>
          {inquiryData.length === 0 && <D.NoDataText>등록된 문의사항이 없습니다.</D.NoDataText>}
          <DataTable
            data={inquiryData}
            columns={columns}
            onChange={handlePaginationChange}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalCount,
            }}
            tableTitle="문의 사항"
          />
        </D.TableContainer>
      </D.Wrapper>
    </>
  );
}

export default Inquiry;
